import React, { useState, useCallback } from 'react';
import { playbookApi } from '../../api/playbookApi';
import type { TransitionForm } from '../../types/playbook.types';

interface FormListPanelProps {
  transitionId: string;
  forms: TransitionForm[];
  onRefresh: () => void;
}

const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';

const FormListPanel: React.FC<FormListPanelProps> = ({ transitionId, forms, onRefresh }) => {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDef, setNewDef] = useState('{\n  "fields": []\n}');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDef, setEditDef] = useState('');

  const handleAdd = useCallback(async () => {
    try {
      const parsed = JSON.parse(newDef);
      await playbookApi.addForm(transitionId, {
        title: newTitle || undefined,
        formDefinition: parsed,
      });
      setAdding(false);
      setNewTitle('');
      setNewDef('{\n  "fields": []\n}');
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, newTitle, newDef, onRefresh]);

  const handleUpdate = useCallback(async (formId: string) => {
    try {
      const parsed = JSON.parse(editDef);
      await playbookApi.updateForm(transitionId, formId, {
        title: editTitle || undefined,
        formDefinition: parsed,
      });
      setEditingId(null);
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, editTitle, editDef, onRefresh]);

  const handleRemove = useCallback(async (formId: string) => {
    try {
      await playbookApi.removeForm(transitionId, formId);
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, onRefresh]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h5 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Forms ({forms.length})</h5>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="text-[9px] text-lex-accent hover:text-lex-accent-hover">+ Add Form</button>
        )}
      </div>

      {forms.map((f) => (
        <div key={f.id} className="rounded border border-gray-200 dark:border-gray-700 p-2" style={{ animation: 'fadeSlideIn 200ms ease-out' }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">{f.title || 'Untitled Form'}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => { setEditingId(f.id === editingId ? null : f.id); setEditTitle(f.title ?? ''); setEditDef(JSON.stringify(f.formDefinition, null, 2)); }} className="text-[9px] text-lex-accent hover:text-lex-accent-hover">
                {editingId === f.id ? 'Close' : 'Edit'}
              </button>
              <button type="button" onClick={() => handleRemove(f.id)} className="text-[9px] text-rose-500 hover:text-rose-700">Remove</button>
            </div>
          </div>
          {editingId === f.id && (
            <div className="mt-2 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-2">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">Title</label>
                <input type="text" className={inputClass} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">Form Definition (JSON)</label>
                <textarea className={`${inputClass} font-mono`} rows={6} value={editDef} onChange={(e) => setEditDef(e.target.value)} />
              </div>
              <button type="button" onClick={() => handleUpdate(f.id)} className="rounded bg-lex-accent px-3 py-1 text-[10px] font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">Save</button>
            </div>
          )}
        </div>
      ))}

      {adding && (
        <div className="rounded border border-lex-accent p-2 bg-lex-accent-soft" style={{ animation: 'coverageDrillDown 200ms ease-out' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-lex-accent">New Form</span>
            <button type="button" onClick={() => setAdding(false)} className="text-[9px] text-gray-400 hover:text-gray-600">Cancel</button>
          </div>
          <div className="space-y-2">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">Title</label>
              <input type="text" className={inputClass} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Form title" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">Form Definition (JSON)</label>
              <textarea className={`${inputClass} font-mono`} rows={6} value={newDef} onChange={(e) => setNewDef(e.target.value)} />
            </div>
          </div>
          <button type="button" onClick={handleAdd} className="mt-2 rounded bg-lex-accent px-3 py-1 text-[10px] font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">Add Form</button>
        </div>
      )}
    </div>
  );
};

export default FormListPanel;
